'use client';

import React from 'react';
import { Typography } from 'src/components';
import {
  faExternalLink,
  faTrashCanXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';
import { CRMObject } from './types';
import { useAuth } from 'src/context/auth';
import { canUnlinkCRMRecord } from 'src/components/PermissionGuard/checks';

interface CRMLinkItemProps {
  crmObject: CRMObject;
  onDelete?: (crmLink: CRMObject) => void;
}

const CRMLinkItem: React.FC<CRMLinkItemProps> = ({ crmObject, onDelete }) => {
  const isThirdParty = crmObject.crm?.toLowerCase() === 'third_party';
  const crmDisplayName = isThirdParty ? 'CRM' : sentenceCase(crmObject.crm);
  const { user } = useAuth();
  const canUnlink = canUnlinkCRMRecord(user);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(crmObject);
    }
  };

  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0 flex-1">
          {crmObject.crm_status && (
            <Typography className="text-primary !text-[.75rem] leading-4">
              {crmObject.crm_status}
            </Typography>
          )}
          <Typography className="text-tertiary !text-[0.75rem] leading-4">
            Owner:{' '}
            {crmObject.owner?.firstname && crmObject.owner?.lastname
              ? `${crmObject.owner.firstname} ${crmObject.owner.lastname}`
              : 'Unknown'}
          </Typography>
          <a
            href={crmObject.crm_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-700 hover:text-sky-800 cursor-pointer text-sm font-semibold pb-2 hover:underline leading-4"
          >
            View in {crmDisplayName || 'CRM'}
            <Icon
              className="pl-1"
              height={12}
              width={12}
              icon={faExternalLink}
            />
          </a>
        </div>
        {canUnlink && onDelete && (
          <button
            onClick={handleDeleteClick}
            className="flex-shrink-0 p-1 text-neutral-500 hover:text-red-600 transition-colors"
            aria-label="Delete CRM link"
            data-testid={`delete-crm-link-${crmObject.id}`}
          >
            <Icon icon={faTrashCanXmark} className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CRMLinkItem;
